import React, { useContext } from "react";
import styled from "styled-components";
import { AuthContext } from "../../../context/AuthContext";
import { formatCNPJ, formatCPF, formatNumber } from "../../../assets/utils";
import moment from "moment-timezone";

const PDFContainer = styled.div`
  width: 800px;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 1000px) {
    width: 100%;
    box-sizing: border-box;
    padding: 20px 30px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 1000px) {
    margin-bottom: 40px;
  }
`;

const Title = styled.h1`
  font-size: 20px; /* Ajustado para caber melhor o título longo */
  margin: 0;
  text-transform: uppercase;
  font-weight: 700;

  @media (max-width: 1000px) {
    font-size: 18px;
    font-weight: 600;
  }
`;

const Content = styled.div`
  line-height: 1.5;
  text-align: justify;
  font-size: 12px; /* Fonte ligeiramente menor para comportar o texto extenso */
  color: #000;

  &.maisProLado {
    padding-left: 30px;
  }

  h3 {
    margin-top: 20px;
    margin-bottom: 10px;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 800;
    text-align: center;
  }

  h4 {
    margin-top: 15px;
    margin-bottom: 5px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  p {
    margin-bottom: 10px;
  }

  ul {
    padding-left: 20px;
    margin: 5px 0;
  }

  li {
    margin-bottom: 5px;
  }
`;

const Signature = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  align-items: end;

  @media (max-width: 1000px) {
    display: flex;
    justify-content: center;
    gap: 20px;
    box-sizing: border-box;
    flex-wrap: wrap;
  }
`;

const SignatureBlock = styled.div`
  text-align: center;
  width: 45%;

  @media (max-width: 1000px) {
    width: 100%;
  }

  & > div:nth-child(2) {
    border-bottom: 1px solid black;
    margin-top: 0px;
    margin-bottom: 5px;
    width: 100%;
    height: 1px;
  }

  .borderLine {
    margin-top: 20px;
  }
`;

const AssinaturaGolden = styled.img`
  width: 200px;
`;

const HeaderImage = styled.div`
  width: 100%;
  margin-bottom: 20px;

  img {
    width: 100%;
  }
`;

const FooterImage = styled.div`
  width: 100%;
  margin-top: 20px;

  img {
    width: 100%;
  }
`;

const PDFGenerator = ({ ContratoData, assinatura, lastId, dataCtr }) => {
  const { userData } = useContext(AuthContext);

  const handleCNPJ = () => {
    if (!userData || !userData.cpfCnpj) return "";
    var fm = userData.cpfCnpj
      .trim()
      .replace(".", "")
      .replace("-", "")
      .replace(".", "")
      .replace("/", "");
    if (fm.length === 11) {
      return formatCPF(userData.cpfCnpj);
    } else if (fm.length === 14) return formatCNPJ(userData.cpfCnpj);
    else return fm;
  };

  const dataAtual = moment.tz("America/Sao_Paulo").format("DD/MM/YYYY");

  function formatarData(dataString) {
    const meses = [
      "janeiro",
      "fevereiro",
      "março",
      "abril",
      "maio",
      "junho",
      "julho",
      "agosto",
      "setembro",
      "outubro",
      "novembro",
      "dezembro",
    ];

    const [dia, mes, ano] = dataString.split("/");
    const nomeMes = meses[parseInt(mes, 10) - 1];

    return `${dia} de ${nomeMes} de ${ano}`;
  }

  return (
    <PDFContainer>
      <HeaderImage>
        <img src="./cabecalho.png" alt="Cabeçalho" />
      </HeaderImage>

      <Header>
        <Title>CONTRATO DE PARTICIPAÇÃO EM OPERAÇÕES MINERAIS - CPOM</Title>
      </Header>

      <Content>
        <p>Pelo presente instrumento particular, as PARTES:</p>

        <p>
          <strong>1. HOLDING GOLDEN BRASIL/SA</strong>, sociedade constituída
          sob as leis da República Federativa do Brasil, registrada sob o{" "}
          <strong>CNPJ 42.007.698/0001-17</strong>, com sede e foro na 3ª
          Avenida, n° 2538, Centro, Balneário Camboriú/SC, portadora do meio de
          comunicação sac@goldenbrasil.com.br, nesse ato representada pelo seu
          representante legal, doravante denominada <strong>"VENDEDORA"</strong>
          , e de outro lado;
        </p>

        <p>
          <strong>2. O ADQUIRENTE {userData.name}</strong>, inscrito(a) no
          CPF/CNPJ <strong>{handleCNPJ()}</strong>, pessoa física ou jurídica,
          capaz, devidamente qualificada conforme documentos em anexo,
          interessada em firmar o presente CONTRATO, a qual preencheu
          devidamente o cadastro na plataforma da VENDEDORA e encaminhou os seus
          respectivos documentos, doravante denominado simplesmente{" "}
          <strong>"ADQUIRENTE"</strong>;
        </p>

        <p>
          Sendo ambas as partes designadas, em conjunto, como "PARTES", e
          isoladamente como "PARTE".
        </p>

        <h4>CONSIDERAÇÕES PRELIMINARES:</h4>
        <p>
          1. Considerando que a VENDEDORA, nos termos da legislação em vigor,
          dispõe de uma plataforma especializada na compra e venda de minérios e
          pedras preciosas, se dedica à mineração, comércios de minérios, pedras
          preciosas, fabricação de joias e administração de bens, sendo
          conhecedor do mercado de minérios e pedras preciosas;
        </p>
        <p>
          2. Considerando que o ADQUIRENTE declara possuir plena capacidade
          civil, dispondo de todas as faculdades necessárias para firmar este
          CONTRATO e assumir as obrigações aqui previstas.
        </p>
        <p>
          As partes celebram o presente "CONTRATO DE PARTICIPAÇÃO EM OPERAÇÕES
          MINERAIS" que se regerá pelas seguintes cláusulas e condições:
        </p>

        <h3>CLÁUSULA PRIMEIRA - DO OBJETO DO CONTRATO</h3>
        <p>
          <strong>ARTIGO 1º</strong> O presente contrato tem por objeto
          formalizar a participação do ADQUIRENTE nas operações minerais
          conduzidas pela VENDEDORA, incluindo prospecção, aquisição,
          beneficiamento, logística, tratamento, processamento, comercialização
          e movimentação de minérios, pedras naturais e ativos minerais
          correlatos.
        </p>
        <p>
          <strong>ARTIGO 2º</strong> A VENDEDORA atuará como gestora exclusiva
          do ciclo produtivo e comercial dos minérios, abrangendo: 1- extração e
          separação; 2 - lapidação; 3- transformação em joias; 4 -
          administração, exposição, marketing e precificação; 5- venda final a
          consumidores ou terceiros interessados.
        </p>
        <p>
          <strong>ARTIGO 3º DA DISTRIBUIÇÃO DE ROYALTIES</strong> <br />O
          ADQUIRENTE terá direito a:
          <br />
          1- Royalties mensais distribuídos proporcionalmente à participação
          contratada, decorrentes das operações comerciais minerais
          administrados pela VENDEDORA, sendo 2,1% à 4,1% à base operacional
          variável conforme o anexo.
          <br />
          2 - Royalties anuais estruturados conforme anexo, vinculados ao
          desempenho consolidado anual da cadeia mineral;
          <br />
          3- Royalties extras, vinculados à abertura de novas frentes de
          extração, expansão operacional, performance de vendas ou aumento de
          capacidade produtiva.
        </p>
        <p>
          <i>
            Parágrafo único: Os royalties representam divisão de resultados
            comerciais oriundos das operações minerais e não constituem
            rendimento financeiro, promessa de retorno, percentual fixo,
            rentabilidade pré-definida ou atividade sujeita à CVM ou BACEN.
          </i>
        </p>

        <h3>CLÁUSULA SEGUNDA: DO VALOR</h3>
        <p>
          <strong>ARTIGO 4º</strong> O valor da participação nas operações
          minerais será fixado de acordo com as condições de mercado no momento
          da venda e com as condições estipuladas na proposta de contratação, a
          qual o ADQUIRENTE manifesta expressa e formalmente a aceitação do
          preço e condições, haja vista a volatilidade desse mercado.
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> O valor de cada contrato de
          participação nas operações minerais CPOM será de R$ 425,00
          (quatrocentos e vinte e cinco reais) sendo a compra mínima de 10
          contratos (sendo que em Janeiro de 2026 passará a vigorar o mínimo de
          50 contratos CPOM). A presente compra será pago o total da
          participação nas operações minerais com{" "}
          <strong>{ContratoData.qttContratos}</strong> contratos no valor de{" "}
          <strong>
            R${" "}
            {formatNumber(
              ContratoData.valorPorContrato * ContratoData.qttContratos
            )}
          </strong>{" "}
          que será pago à vista, por meio de transferência bancária realizada no
          ato da assinatura deste contrato, diretamente para a conta indicada
          pela VENDEDORA. Considerar-se-á cumprida a obrigação de pagamento
          mediante apresentação do comprovante da transferência.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> O valor indicado no parágrafo
          anterior corresponde ao total da aquisição da participação nas
          operações minerais, objeto deste instrumento, sendo o pagamento
          efetuado de forma una e global conforme pactuado entre as partes.
        </p>
        <p>
          <strong>Parágrafo Terceiro:</strong> Os Royalties decorrentes deste
          contrato serão pagos exclusivamente por meio de transferência
          bancária, utilizando-se os dados cadastrados, sob titularidade de{" "}
          <strong>{userData.name}</strong> inscrito(a) sob o CNPJ/CPF nº{" "}
          <strong>{handleCNPJ()}</strong> conforme informada no Anexo I deste
          instrumento. Fica vedado qualquer pagamento para conta diversa sem
          autorização expressa e por escrito da ADQUIRENTE.
        </p>

        <h3>CLÁUSULA TERCEIRA: DAS DECLARAÇÕES E CIÊNCIAS</h3>
        <p>
          <strong>ARTIGO 5º</strong> A VENDEDORA declara, e o ADQUIRENTE
          expressamente reconhece, que o presente contrato possui natureza
          estritamente comercial, destinado à administração das operações
          minerais, lapidação, fabricação e comercialização de minérios e joias,
          conforme atividades regularmente registradas nos CNAEs da VENDEDORA.
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> A VENDEDORA não exerce atividades
          de custódia de valores, não realiza operações de crédito, não faz
          intermediação de ativos, não administra carteiras, fundos ou
          instrumentos financeiros, tampouco presta qualquer serviço típico de
          instituição financeira, securitária ou de investimento, atua com
          processos de extração, compra, custódia, comercialização e destinação
          conforme normas vigentes do setor de mineração de minérios.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> Os Royalties previstos neste
          contrato decorrem exclusivamente dos serviços de administração das
          operações minerais, com processos de extração, compra, custódia de
          minérios, comercialização e destinação vinculadas a participação das
          operações minerais do ADQUIRENTE conforme normas vigentes do setor,
          constituindo participação nos resultados comerciais da gestão de bens
          minerais, e não rendimento financeiro, aplicação de recursos, operação
          bancária, securitária ou oferta de valores mobiliários.
        </p>
        <p>
          <strong>Parágrafo Terceiro:</strong> A VENDEDORA declara que não está
          sujeita à supervisão do Banco Central do Brasil (BACEN) nem da
          Comissão de Valores Mobiliários (CVM), por não desenvolver atividade
          financeira, de crédito, securitária, de captação de recursos ou de
          emissão/oferta pública de valores mobiliários, atuando exclusivamente
          como empresa comercial e industrial do setor mineral.
        </p>
        <p>
          <strong>Parágrafo Quarto:</strong> As PARTES comprometem-se a cumprir
          integralmente a legislação brasileira aplicável, especialmente a Lei
          n° 9.613/1998, com suas alterações, a Lei n° 13.974/2020, a Lei n°
          14.478/2022, o Decreto n° 9.663/2019, a Resolução COAF n° 36/2021 e a
          Lei n° 13.810/2019, bem como todas as normas regulatórias expedidas
          pelos órgãos competentes.
        </p>
        <p>
          <strong>Parágrafo Quinto:</strong> A VENDEDORA declara adotar práticas
          de compliance, governança e rastreabilidade comercial,
          comprometendo-se a manter registros adequados das operações e a
          prestar todas as informações eventualmente solicitadas por autoridades
          competentes.
        </p>
        <p>
          <strong>Parágrafo Sexto:</strong> O ADQUIRENTE declara que os recursos
          utilizados na contratação têm origem lícita, responsabilizando-se
          integralmente pela veracidade das informações e documentos
          eventualmente apresentados.
        </p>
        <p>
          <strong>Parágrafo Sétimo:</strong> O ADQUIRENTE obriga-se a utilizar a
          plataforma e os canais da VENDEDORA em estrita conformidade com a
          legislação vigente, especialmente as Leis n° 9.613/1998 e n°
          13.260/2016 abstendo-se de qualquer prática que viole normas penais,
          regulatórias, princípios de boa-fé, moralidade comercial ou que possa
          comprometer a regularidade das operações.
        </p>

        <h3>CLÁUSULA QUARTA: DO CADASTRO</h3>
        <p>
          <strong>ARTIGO 6º</strong> Antes de iniciar seu relacionamento com a
          VENDEDORA, o ADQUIRENTE deverá fornecer todas as informações
          cadastrais solicitadas, enviando-as para a VENDEDORA, declarando estar
          ciente e concorda que é de sua exclusiva responsabilidade manter seus
          dados cadastrais permanentemente atualizados perante a VENDEDORA,
          comprometendo-se a comunicar, de imediato e por escrito, qualquer
          alteração de endereço, telefone, e-mail, documentos pessoais, dados
          bancários ou demais informações relevantes.
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> A VENDEDORA poderá recusar,
          suspender ou deixar de processar qualquer solicitação ou ordem do
          ADQUIRENTE que não estiver devidamente cadastrado ou cujos dados
          estejam desatualizados, sem que isso caracterize descumprimento
          contratual ou gere direito a indenização.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> A ausência de atualização
          cadastral poderá, ainda, ensejar a suspensão temporária de pagamentos
          dos Royalties, até a completa regularização do cadastro do ADQUIRENTE
          junto à VENDEDORA.
        </p>

        <h3>CLÁUSULA QUINTA - DAS OBRIGAÇÕES DAS PARTES</h3>
        <p>
          <strong>ARTIGO 7º</strong> A VENDEDORA e o ADQUIRENTE assumem, cada
          qual, as obrigações previstas nesta cláusula, indispensáveis ao
          correto funcionamento da relação comercial estabelecida, que tem por
          objeto a administração das operações minerais, com processos de
          extração, compra, custódia de minérios, comercialização e destinação
          vinculadas a participação das operações minerais do ADQUIRENTE
          conforme normas vigentes do setor venda de minérios.
        </p>
        <p>
          <strong>Parágrafo Primeiro: OBRIGAÇÕES DA VENDEDORA</strong>
          <br />
          a) administrar, com exclusividade, todo o ciclo operacional dos
          minérios vinculados ao contrato, incluindo administração das operações
          minerais, lapidação, fabricação das joias e comercialização a
          terceiros, aplicando sua estrutura técnica, industrial e comercial;
          <br />
          b) realizar a distribuição mensal de Royalties da participação
          operacional equivalente à aquisição entre 2,1% á 4,1% ao ADQUIRENTE
          (conforme a modalidade contratada em anexo) calculado sobre a base
          operacional interna, a título de participação nos resultados da
          administração, fabricação e venda das joias e dos minérios, pelo prazo
          de 36 (trinta e seis) meses;
          <br />
          c) observar integralmente a legislação aplicável, especialmente normas
          de prevenção à lavagem de dinheiro e financiamento ao terrorismo,
          compliance e integridade corporativa;
          <br />
          d) manter registros organizados e procedimentos internos que permitam
          a fiscalização e rastreabilidade das operações, fornecendo às
          autoridades competentes as informações legalmente exigidas.
        </p>
        <p>
          <strong>Parágrafo Segundo: OBRIGAÇÕES DO ADQUIRENTE</strong>
          <br />
          a) fornecer informações e documentos verdadeiros, respondendo
          integralmente por eventual falsidade, omissão ou incorreção;
          <br />
          b) cumprir toda a legislação aplicável, especialmente normas de
          prevenção à lavagem de dinheiro e financiamento ao terrorismo, bem
          como as regras deste contrato e os procedimentos operacionais da
          VENDEDORA;
          <br />
          c) responsabilizar-se pela licitude da origem dos recursos empregados
          na contratação;
          <br />
          d) reconhecer que os Royalties não constituem rendimento financeiro,
          investimento, aplicação, operação bancária ou securitária, mas
          exclusivamente participação nas operações minerais;
          <br />
          e) manter seus dados cadastrais permanentemente atualizados, sob pena
          de suspensão da distribuição dos Royalties mensais até a
          regularização;
          <br />
          f) não imputar à VENDEDORA qualquer responsabilidade por expectativas
          de valorização dos minérios, joias ou resultados comerciais, ciente de
          que tais atividades estão sujeitas à variação de mercado.
        </p>
        <p>
          <strong>ARTIGO 8º</strong> Na hipótese de ocorrência de fiscalização,
          bloqueio, suspensão de contas bancárias, interrupção de recebimentos,
          restrições operacionais ou quaisquer outras medidas determinadas por
          autoridades competentes, decorrentes do cumprimento da legislação e
          das normas regulatórias vigentes, os pagamentos relativos à
          distribuição de Royalties mensais poderão ser temporariamente
          suspensos até a plena regularização da situação, caracterizando-se tal
          evento como hipótese de força maior, para todos os fins contratuais.
          <br />
          a) Caso a suspensão perdure por período de até 1 (um) mês, não
          incidirá qualquer atualização sobre os valores correspondentes.
          <br />
          b) Se ultrapassar esse prazo, os valores serão atualizados
          monetariamente pelo INPC, até a data do efetivo pagamento, sem
          acréscimo de juros, multas ou penalidades, reconhecendo-se igualmente
          a situação como evento de força maior.
        </p>

        <h3>CLÁUSULA SEXTA - DAS RESPONSABILIDADE DA VENDEDORA</h3>
        <p>
          <strong>ARTIGO 9º</strong> A responsabilidade da VENDEDORA limita-se
          estritamente às atividades de administração de operações minerais, com
          processos de extração, compra, custódia de minérios, comercialização e
          destinação vinculadas a participação das operações minerais ao
          ADQUIRENTE, conforme previsto neste contrato. Em nenhuma hipótese a
          VENDEDORA responderá por: 1- danos indiretos, danos especiais, perda
          de oportunidade, lucros cessantes ou expectativas de ganho de qualquer
          natureza; 2- prejuízos decorrentes de atos ou omissões de terceiros,
          tais como instituições financeiras, plataformas de pagamento,
          transportadoras, seguradoras, fornecedores, prestadores de serviços ou
          agentes externos envolvidos no ciclo operacional; 3- responsabilidades
          não expressamente previstas neste contrato ou que ultrapassem o escopo
          técnico e comercial da administração das operações minerais, venda dos
          minérios e da fabricação e venda das joias.
        </p>
        <p>
          <strong>ARTIGO 10</strong> A VENDEDORA não poderá ser responsabilizada
          por eventos de caso fortuito ou força maior, nos termos do art. 393 do
          Código Civil, incluindo, mas não se limitando a: 1- determinações
          governamentais que impeçam, limitem ou modifiquem suas atividades; 2-
          pandemias, crises sanitárias ou emergências de saúde pública; 3-
          bloqueios, fiscalizações ou restrições regulatórias; 4- desastres
          naturais; 5- greves, paralisações ou interrupções de cadeia produtiva;
          6- sanções econômicas ou embargos que afetem o setor mineral ou
          joalheiro; 7- falhas sistêmicas relevantes, interrupções de energia ou
          serviços essenciais.
        </p>
        <p>
          <i>
            Parágrafo único: Em caso de força maior ou caso fortuito que
            inviabilize de forma permanente a continuidade da execução
            contratual, poderá a VENDEDORA promover a rescisão unilateral
            justificada, mediante comunicação formal ao ADQUIRENTE, sem
            penalidades para qualquer das partes.
          </i>
        </p>

        <h3>CLÁUSULA SÉTIMA - DA CESSÃO E SUCESSÃO CONTRATUAL</h3>
        <p>
          <strong>ARTIGO 11</strong> O ADQUIRENTE poderá a qualquer momento
          ceder ou vender a terceiros sua participação nas operações minerais
          prevista neste contrato, desde que mediante anuência expressa da
          VENDEDORA.
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> Caso a venda seja realizada à
          VENDEDORA, ocorrerá após o decurso do prazo de 36 (trinta e seis)
          meses, pelo mesmo valor de aquisição, observado o prazo máximo de 30
          (trinta) dias úteis após o término do contrato para o pagamento.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> A VENDEDORA poderá recusar a
          cessão caso o terceiro não preencha os requisitos cadastrais, legais
          ou de compliance exigidos por suas políticas internas, não cabendo ao
          ADQUIRENTE ou ao cessionário qualquer direito de indenização.
        </p>
        <p>
          <strong>Parágrafo Terceiro:</strong> A VENDEDORA não responderá por
          eventuais litígios, cobranças ou responsabilidades decorrentes de
          negociações particulares entre o ADQUIRENTE e terceiros, limitando-se
          ao reconhecimento da cessão após sua aprovação formal.
        </p>
        <p>
          <strong>ARTIGO 12</strong> Em caso de falecimento do ADQUIRENTE, os
          direitos e obrigações decorrentes deste contrato poderão ser
          transmitidos diretamente aos herdeiros legais, desde que apresentada
          documentação hábil que comprove a qualidade de herdeiro (formal de
          partilha, escritura pública de inventário, alvará judicial ou outro
          documento legalmente aceito).
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> Os herdeiros que assumirem a
          posição contratual do ADQUIRENTE deverão manter seus dados cadastrais
          atualizados junto à VENDEDORA, tornando-se responsáveis pelo fiel
          cumprimento deste contrato.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> A VENDEDORA não responderá por
          disputas ou controvérsias entre herdeiros, limitando-se a reconhecer
          como sucessores aqueles que constarem no formal de partilha, ou
          escritura pública de inventário ou no alvará judicial.
        </p>
        <p>
          <strong>Parágrafo Terceiro:</strong> Até a efetiva regularização da
          sucessão contratual, os pagamentos dos Royalties ficaram suspensos,
          retomando-se tão logo ocorra a habilitação formal dos herdeiros
          perante a VENDEDORA.
        </p>

        <h3>CLÁUSULA OITAVA - DOS PRAZO E RESCISÃO</h3>
        <p>
          <strong>ARTIGO 13</strong> O presente CONTRATO entra em vigor na data
          da confirmação do cadastro do ADQUIRENTE, do envio do comprovante da
          transferência bancária, do envio dos documentos, e da aceitação
          eletrônica deste instrumento, permanecendo vigente pelo prazo de 36
          (trinta e seis) meses.
        </p>
        <p>
          <strong>ARTIGO 14</strong> A VENDEDORA não será responsável por
          prejuízos decorrentes de rescisão forçada motivada por eventos de
          força maior ou caso fortuito, nos termos do art. 393 do Código Civil,
          aplicando-se, nesse caso, o procedimento previsto no artigo 11 deste
          contrato.
          <br />
          a) Na hipótese de rescisão por impossibilidade definitiva de
          continuidade da execução contratual decorrente de força maior, a
          liquidação contratual será limitada aos valores proporcionalmente
          devidos até a data do evento, sem devolução de capital, sem
          restituições integrais e sem garantia de continuidade das
          participações futuras.
        </p>
        <p>
          <strong>Parágrafo Único:</strong> Findo o prazo de 36 (trinta e seis)
          meses de execução das atividades de administração das operações
          minerais, com processos de extração, compra, custódia de minérios,
          comercialização e destinação vinculadas a participação das operações
          minerais considera-se concluído o ciclo comercial previsto neste
          instrumento.
        </p>

        <h3>CLÁUSULA NONA - DAS COMUNICAÇÕES E NOTIFICAÇÕES</h3>
        <p>
          <strong>ARTIGO 15</strong> Todos os avisos, comunicações e
          notificações relativos a este CONTRATO deverão ser formulados por
          escrito. O ADQUIRENTE declara ciência e concorda que as comunicações
          da VENDEDORA serão realizadas exclusivamente por e-mail, enviado ao
          endereço eletrônico informado no cadastro (ou posteriormente
          atualizado), considerando-se válidas e eficazes todas as mensagens
          remetidas a tal e-mail ou WhatsApp. Para contato institucional, a
          VENDEDORA disponibiliza o endereço eletrônico: sac@goldenbrasil.com.br
          e WhatsApp n. 0800 606 6048.
        </p>
        <p>
          <strong>Parágrafo Primeiro:</strong> Poderão ser utilizados, como
          meios adicionais de comprovação de envio e recebimento, os logs de
          servidores, protocolos de disparo e de abertura, bem como os registros
          eletrônicos da plataforma contratual, os quais terão valor probatório
          entre as partes.
        </p>
        <p>
          <strong>Parágrafo Segundo:</strong> Qualquer contranotificação ou
          resposta às comunicações recebidas deverá ser encaminhada à outra
          PARTE no prazo máximo de 30 (trinta) dias úteis, contados da data do
          recebimento da comunicação, sob pena de presunção de anuência tácita
          aos termos nela contidos.
        </p>

        <h3>CLÁUSULA DÉCIMA - DA CAPACIDADE E VINCULAÇÃO</h3>
        <p>
          <strong>ARTIGO 16</strong> As PARTES declaram e garantem possuir
          capacidade civil para firmar o presente contrato ou poderes
          suficientes para agir em nome da PARTE que representa, vinculando-a
          integralmente, bem como todos aqueles que venham a apresentar
          reivindicações em seu nome, nos termos deste instrumento.
        </p>

        <h3>
          CLÁUSULA DÉCIMA PRIMEIRA - DA SOLIDEZ OPERACIONAL, PATRIMONIAL E DA
          SEGURANÇA CONTRATUAL
        </h3>
        <p>
          A VENDEDORA assegura ao ADQUIRENTE que todas as operações minerais
          vinculadas a este contrato encontram-se amparadas por documentação
          técnica, regularidade institucional, lastro patrimonial e
          rastreabilidade operacional, assegurando plena transparência e
          integridade comercial.
        </p>
        <p>
          <strong>1- Da Estrutura Operacional</strong> <br />
          Todas as atividades realizadas pela VENDEDORA são sustentadas por
          processos internos documentados, registros minerários ativos, licenças
          ambientais válidas e documentação perante a Agência Nacional de
          Mineração, Laboratórios GIA / IGL / Geosol e outros, assegurando ao
          ADQUIRENTE que sua participação está vinculada a operações reais,
          legais e devidamente estruturadas.
        </p>
        <p>
          <strong>2- Do Lastro Patrimonial</strong> <br />A participação
          comercial adquirida está associada ao conjunto de ativos reais da
          HOLDING GOLDEN BRASIL S.A., composto por áreas de mineração avaliadas,
          acervos minerais documentados, joias e diamantes certificados,
          maquinário industrial, patrimônio operacional, ativos comerciais,
          investimentos internacionais, e valuation empresarial, sendo lastro de
          milhões que vem assegurando suporte empresarial robusto.
        </p>
        <p>
          <strong>3- Da Rastreabilidade e Transparência</strong> <br />
          Todas as movimentações comerciais e operacionais possuem
          rastreabilidade documental, com registros internos, notas fiscais,
          relatórios e extratos comerciais disponíveis ao ADQUIRENTE por meio
          dos sistemas oficiais da VENDEDORA.
        </p>
        <p>
          <strong>4- Do Sistema de Acompanhamento</strong> <br />O ADQUIRENTE
          terá acesso ao Aplicativo Oficial Golden Brasil, onde poderá
          acompanhar, em ambiente seguro, sua participação nas operações,
          visualização de royalties, relatórios comerciais e informações
          operacionais relevantes.
        </p>
        <p>
          <strong>5- Da Observância Legal e Institucional</strong> <br />A
          VENDEDORA mantém plena conformidade com a legislação civil, comercial,
          minerária, ambiental e de proteção de dados, proporcionando ao
          ADQUIRENTE respaldo jurídico institucional e segurança operacional
          durante toda a vigência contratual.
        </p>
        <p>
          <i>
            Parágrafo único. A presente cláusula reforça a integridade, solidez
            e rastreabilidade da participação comercial adquirida,
            proporcionando ao ADQUIRENTE segurança jurídica e operacional plena,
            sem caracterizar qualquer promessa de resultado financeiro.
          </i>
        </p>

        <h3>CLÁUSULA DÉCIMA SEGUNDA - SEGURANÇA JURÍDICA</h3>
        <p>
          <strong>ARTIGO 17</strong> Este contrato é um título executivo
          extrajudicial que em caso de adversidades poderá ser executado, sendo
          acionado na forma da lei civil, mediante avaliação técnica
          independente e observância dos procedimentos de execução aplicável.
        </p>
        <p>
          <strong>Parágrafo único:</strong> A Golden Brasil declara que esta
          participação está vinculada a uma operação real, com base documental,
          ativos, lastro patrimonial e estrutura empresarial existente. Para
          fins de transparência, o ADQUIRENTE poderá solicitar, de forma
          justificada, comprovação institucional da existência da operação,
          incluindo certificados, registros ou documentos resumidos, sem caráter
          detalhado e sigiloso.
        </p>

        <h3>
          CLÁUSULA DÉCIMA TERCEIRA - CONFIDENCIALIDADE, PROTEÇÃO DE INFORMAÇÕES
          E USO RESTRITO
        </h3>
        <p>
          <strong>1. Confidencialidade:</strong> Todas as informações,
          documentos, termos, comunicações, contratos e conteúdos fornecidos
          pela GOLDEN BRASIL ao PARTICIPANTE são classificados como
          confidenciais, independentemente do formato (digital, físico, verbal
          ou institucional).
        </p>
        <p>
          <strong>2. Uso Restrito:</strong> O PARTICIPANTE reconhece que este
          contrato e seu conteúdo são exclusivos e de uso pessoal, sendo
          expressamente proibido: copiar, digitalizar ou fotografar o documento;
          encaminhar ou compartilhar com terceiros, advogados externos, grupos
          de mensagens ou redes sociais; exibir publicamente, total ou
          parcialmente; utilizar para comparação, divulgação, análise externa ou
          qualquer finalidade que não seja a execução contratual.
        </p>
        <p>
          <strong>3. Solicitação de análise:</strong> Qualquer dúvida, revisão
          ou necessidade de esclarecimento deverá ser realizada exclusivamente
          pelos canais oficiais da GOLDEN BRASIL, mediante consultores
          autorizados.
        </p>
        <p>
          <strong>4. Proteção de dados do PARTICIPANTE:</strong> A GOLDEN BRASIL
          se compromete a tratar os dados pessoais e documentos fornecidos pelo
          PARTICIPANTE em conformidade com a LGPD (Lei 13.709/2018), aplicando
          medidas administrativas e tecnológicas para proteção,
          confidencialidade e segurança das informações.
        </p>
        <p>
          <strong>5. Penalidade em caso de violação:</strong> O PARTICIPANTE
          concorda que o descumprimento desta cláusula configura quebra
          contratual e violação de sigilo, sujeitando-o automaticamente a: multa
          compensatória equivalente a 10% (dez por cento) do valor total
          contratado, sem prejuízo de: cancelamento do contrato sem restituição;
          responsabilidade civil por danos causados; medidas judiciais cabíveis.
        </p>
        <p>
          <strong>6. Vigência da obrigação de sigilo:</strong> Esta obrigação
          permanece válida mesmo após o término, cancelamento ou conclusão deste
          contrato, por prazo indeterminado.
        </p>

        <h3>
          CLÁUSULA DÉCIMA QUARTA - DA IRREVOGABILIDADE E IRRETRATABILIDADE
        </h3>
        <p>
          <strong>ARTIGO 18</strong> Este CONTRATO é celebrado em caráter
          irrevogável e irretratável, observadas as hipóteses legais e
          contratuais de rescisão previstas neste instrumento, expressando,
          segundo seus termos e condições, a mais ampla vontade das PARTES.
        </p>

        <h3>
          CLÁUSULA DÉCIMA QUINTA - DA NULIDADE PARCIAL (CLÁUSULA DE
          SALVATORIEDADE)
        </h3>
        <p>
          <strong>ARTIGO 19</strong> A eventual nulidade, invalidade ou
          inexequibilidade de qualquer disposição deste CONTRATO não prejudicará
          as demais, que permanecerão válidas e plenamente eficazes. As PARTES
          envidarão esforços para substituir a disposição afetada por outra que
          produza, na maior medida possível, efeitos equivalentes.
        </p>

        <h3>CLÁUSULA DÉCIMA SEXTA - DA ASSINATURA E FORMA ELETRÔNICA</h3>
        <p>
          <strong>ARTIGO 20</strong> AS PARTES reconhecem a validade jurídica
          deste CONTRATO celebrado e aceito por meios eletrônicos, inclusive com
          uso de identificadores, certificados digitais, aceite por clique e
          registros eletrônicos (logs), nos termos da legislação aplicável.
        </p>

        <h3>CLÁUSULA DÉCIMA SÉTIMA - DAS ALTERAÇÕES E PREVALÊNCIA</h3>
        <p>
          <strong>ARTIGO 21</strong> Este CONTRATO, suas propostas comerciais e
          eventuais aditivos constituem o acordo integral entre as PARTES sobre
          o objeto, substituindo instrumentos anteriores relacionados ao mesmo
          tema. Qualquer alteração somente terá validade se realizada por
          escrito e aceita eletronicamente pelas PARTES.
        </p>
        <p>
          <strong>ARTIGO 22</strong> Em caso de conflito entre este CONTRATO e
          documentos acessórios, prevalecerá o disposto neste instrumento, salvo
          se o documento acessório for aditivo que expressamente modifique a
          cláusula em conflito.
        </p>

        <h3>CLÁUSULA DÉCIMA OITAVA - DO FORO</h3>
        <p>
          <strong>ARTIGO 23</strong> Para dirimir quaisquer controvérsias do
          contrato as partes elegem o foro da comarca de Balneário Camboriú/SC.
        </p>

        <p style={{ marginTop: "40px", fontWeight: "bold" }}>
          Balneário Camboriú/SC, {ContratoData.dateCreated
            ? formatarData(moment(ContratoData.dateCreated).format("DD/MM/YYYY")) // <--- ALTERAÇÃO AQUI
            : formatarData(dataAtual)}.
        </p>

        <hr style={{ margin: "30px 0" }} />

        <Title>
          TERMO ADITIVO AO CONTRATO DE PARTICIPAÇÃO EM OPERAÇÕES MINERAIS E
          DISTRIBUIÇÃO DE ROYALTIES - СРОМ
        </Title>

        <Content>
          <p>
            Pelo presente TERMO ADITIVO ao CONTRATO DE PARTICIPAÇÃO EM OPERAÇÕES
            MINERAIS E DISTRIBUIÇÃO DE ROYALTIES - CPOM, celebrado entre as
            mesmas PARTES já qualificadas no instrumento principal, resolvem, de
            comum acordo, ajustar, complementar e detalhar os modelos de
            divisão, acúmulo e disponibilidade dos royalties, nos termos abaixo
            descritos, permanecendo inalteradas todas as demais cláusulas do
            contrato original.
          </p>

          <h4>CLÁUSULA PRIMEIRA - DO OBJETO DO TERMO ADITIVO</h4>
          <p>
            1.1. O presente Termo Aditivo tem por objeto disciplinar e organizar
            os modelos de divisão, acúmulo e saque dos royalties comerciais com
            a "CAIXINHA GOLDEN BOX", para o ADQUIRENTE nas operações minerais
            administradas pela VENDEDORA, conforme modalidades de planos
            descritas neste instrumento.
          </p>
          <p>
            1.2. As PARTES reconhecem expressamente que os royalties aqui
            descritos mantêm natureza estritamente comercial, oriundos da
            divisão de resultados das operações minerais, não constituindo
            rendimento financeiro, promessa de retorno, percentual fixo
            garantido, aplicação financeira ou valor mobiliário.
          </p>

          <h4>
            CLÁUSULA SEGUNDA - DA DIVISÃO DE ROYALTIES MENSAIS E MODELOS DE
            SAQUE COM A CAIXINHA GOLDEN BOX
          </h4>
          <p>
            Ficam instituídos os seguintes modelos operacionais de distribuição
            dos royalties, conforme opção contratada pelo ADQUIRENTE:
          </p>
          <p>
            <strong>I - PLANO TRIMESTRAL - SAQUE TRIMESTRAL</strong>
            <br />
            a) Acúmulo mensal de royalties operacionais na proporção de 4,1%,
            calculados sobre a base operacional interna da VENDEDORA;
            <br />
            b) Saque trimestral correspondente ao acumulado de 12,3%;
            <br />
            c) Acúmulo anual de royalties equivalente a 49,2%;
            <br />
            d) Consideradas as operações, ciclos produtivos e distribuição ao
            longo dos 36 (trinta e seis) meses do contrato, o total acumulado
            estimado ao final do contrato poderá atingir até 150%, observado o
            desempenho operacional e comercial das atividades minerais.
            <br />
            e) Saques sempre efetuados nas janelas de saques com pagamentos
            sempre efetivados entre o primeiro dia útil do mês até o quinto dia
            útil do mês subsequente;
          </p>
          <p>
            <strong>
              II - PLANO ANUAL - SAQUE MENSAL COM BENEFÍCIO EXTRA DE 1%
            </strong>
            <br />
            a) Disponibilização de benefício extra com saque mensal de 1%, de
            forma destacada da valorização operacional principal;
            <br />
            b) Saque mensal do benefício extra de 1%, totalizando 12% ao ano;
            <br />
            c) Benefício extra total estimado em 36% ao longo dos 36 (trinta e
            seis) meses;
            <br />
            d) Considerada a soma entre a valorização operacional principal e os
            benefícios extras, o total consolidado estimado poderá alcançar até
            186% ao final do contrato, condicionado aos resultados efetivos das
            operações minerais.
            <br />
            e) Saques sempre efetuados nas janelas de saques com pagamentos
            sempre efetivados entre o primeiro dia útil do mês até o quinto dia
            útil do mês subsequente;
          </p>
          <p>
            <strong>
              III - PLANO TOTAL - SAQUE MENSAL COM BENEFÍCIO EXTRA DE 1,3% E
              ACÚMULO FINAL
            </strong>
            <br />
            a) Disponibilização de benefício extra mensal de 1,3%, de forma
            independente da valorização operacional principal;
            <br />
            b) Saque mensal do benefício extra de 1,3%, totalizando 15,6% ao
            ano;
            <br />
            c) Benefício extra total estimado em 46,8% ao longo do prazo
            contratual;
            <br />
            d) O total consolidado estimado, somando-se a valorização principal
            acumulada ao longo do contrato com os benefícios extras, poderá
            alcançar até 196,8%, de acordo com a performance das operações
            minerais.
            <br />
            e) Saques sempre efetuados nas janelas de saques com pagamentos
            sempre efetivados entre o primeiro dia útil do mês até o quinto dia
            útil do mês subsequente;
          </p>

          <h4>
            CLÁUSULA TERCEIRA - DA SEGREGAÇÃO ENTRE VALORIZAÇÃO PRINCIPAL E
            BENEFÍCIOS EXTRAS
          </h4>
          <p>
            3.1. As PARTES reconhecem que:
            <br />
            a) A valorização operacional principal, variável entre 2,1% a 4,1%,
            mantém sua característica de acúmulo com disponibilidade anual,
            conforme regras do contrato principal;
            <br />
            b) Os benefícios extras mensais, nas proporções de 1% ou 1,3%,
            constituem vantagens adicionais, lançadas de forma segregada,
            facultando ao ADQUIRENTE a possibilidade de saque mensal, conforme
            modalidade contratada;
            <br />
            c) A segregação entre valorização principal e benefícios extras não
            altera a natureza jurídica do contrato, permanecendo ambos como
            participação nos resultados comerciais das operações minerais.
            <br />
            d) A migração de planos para o ADQUIRENTE que estiver no plano
            trimestral poderá ser para o plano anual ou total (sempre
            respeitando as regras de cada plano), sendo que no momento da
            migração deverá escolher no sistema "A CAIXINHA GOLDEN BOX MENSAL OU
            TOTAL" onde o aceite das regras do novo plano se dará
            automaticamente após a escolha da CAIXINHA GOLDEN BOX MENSAL OU
            TOTAL;
            <br />
            e) O cliente que mudar de plano de saque de Royalties utilizando a
            Caixinha Golden Box, poderá mudar apenas uma única vez, sendo
            restrito às novas mudanças de plano de saque de Royalties no mesmo
            contrato em andamento.
          </p>

          <h4>CLÁUSULA QUARTA - DA RATIFICAÇÃO</h4>
          <p>
            4.1. Permanecem ratificadas e inalteradas todas as demais cláusulas
            do Contrato CPOM que não conflitam com o presente Termo Aditivo.
          </p>
          <p>
            4.2. Este Termo Aditivo passa a integrar o Contrato CPOM para todos
            os fins de direito, produzindo efeitos a partir de sua aceitação
            eletrônica pelo ADQUIRENTE, nos termos do contrato principal.
          </p>
        </Content>
      </Content>

      <Signature>
        <SignatureBlock>
          <div>
            {assinatura === "assinado"
              ? userData.name + " - " + handleCNPJ()
              : ""}
          </div>
          <div className="borderLine"></div>
          <div>Assinatura do Comprador(a)</div>
        </SignatureBlock>
        <SignatureBlock>
          <AssinaturaGolden
            src="golden-assinatura.png"
            alt="Assinatura Golden"
          />
          <div className="borderLine"></div>
          <div>Assinatura do Vendedor</div>
        </SignatureBlock>
      </Signature>

      <FooterImage>
        <img src="./rodape.png" alt="Rodapé" />
      </FooterImage>
    </PDFContainer>
  );
};

export default PDFGenerator;
